<template>
  <b-badge v-if="payout" :id="containerId" pill class="payout-badge action-links d-inline-flex">
    <span v-if="isRouteUpdate && payout.payout_number" @click.prevent="pushRoute(payout)" style="color: #0052CC;" class="cursor-pointer"
      v-b-tooltip.hover.d100.html="{ variant: 'base', container: containerId }"
      :title="'Edit payout ' + (payout.payout_number ? payout.payout_number : '')">
      <span v-html="editIconSvg" class="action-link-icon"></span>
    </span>
    <a v-else href="#" @click.prevent="showEditPayoutsModal(payout)"
      v-b-tooltip.hover.d100.html="{ variant: 'base', container: containerId }"
      :title="'Edit payout ' + (payout.payout_number ? payout.payout_number : '')">
      <span v-html="editIconSvg" class="action-link-icon"></span>
    </a>
    <a :id="'payout-file-link-' + payout.id" :class="{ 'disabled': !payout.payment_doc_url }" target="_blank"
      :href="`${payout.payment_doc_url}/${item.payout_template_type.name}`" :disabled="!payout.payment_doc_url">
      <span v-html="draftIconSvg" class="action-link-icon"></span>
    </a>

    <b-tooltip :target="'payout-file-link-' + payout.id"
      :title="'View payout ' + payout.payment_doc_file_name "
      variant="base" container="body" placement="top" boundary="viewport"
      :delay="{ show: 300, hide: 0 }"></b-tooltip>

    <a :class="{ 'disabled': payout.payout_type.id === 2 || !payout.worklogs_report_url }" target="_blank"
      :href="payout.worklogs_report_url" v-b-tooltip.html="{ variant: 'base', container: containerId }"
      :disabled="!payout.worklogs_report_url" :title="'Download worklogs report ' + payout.payout_number">
      <span v-html="downloadIconSvg" class="action-link-icon"></span>
    </a>
    <div role="group" class="more-actions-dropdown">
      <b-dropdown :disabled="!Boolean(payout.id)" class="dropdown__theme-light" right variant="link"
        toggle-class="text-decoration-none" no-caret>
        <template v-slot:button-content>
          <span v-html="moreVertIconSvg" class="action-link-icon"></span>
        </template>
        <b-dropdown-item-button @click.prevent="showSendPayoutEmailModal(payout)">
          <span class="dropdown-item-link" :class="{ 'active-action-item': !payout.email_sent_date }">
            <span v-html="mailIconSvg" class="dropdown-icon"></span>
            Send payout {{ payout.email_sent_date ? '(sent)' : '' }}
          </span>
        </b-dropdown-item-button>
        <b-dropdown-item-button @click.prevent="markAsPaid(payout)">
          <span class="dropdown-item-link" :class="{ 'active-action-item': !payout.is_paid }">
            <span v-html="paidIconSvg" class="dropdown-icon"></span>
            Mark as paid {{ payout.is_paid ? '(marked)' : '' }}
          </span>
        </b-dropdown-item-button>
        <b-dropdown-item-button @click.prevent="removePayout(payout)">
          <span class="dropdown-item-link" :class="{ 'active-action-item': !payout.is_paid }">
            <span v-html="removeIconSvg" class="dropdown-icon"></span>
            Remove
          </span>
        </b-dropdown-item-button>
      </b-dropdown>
    </div>

    <FinancePayoutsEmailModal v-model="sendEmailModal.show" :title="'test title'" :submitButtonText="'test'"
      :payoutId="sendEmailModal.payout ? sendEmailModal.payout.id : null" :isLoading="getIsModalLoading"
      @submit="payoutEmailSent" />
  </b-badge>
</template>

<script>
import FinancePayoutsEmailModal from './modals/FinancePayoutsForm/FinancePayoutsEmailModal.vue'
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import editIcon from '@/assets/icons/editIcon'
import { draftIconSvg } from '@/assets/icons/draftIcon'
import { downloadIconSvg } from '@/assets/icons/downloadIcon'
import { moreVertIconSvg } from '@/assets/icons/moreVertIcon'
import { mailIconSvg } from '@/assets/icons/mailIcon'
import removeIcon from '@/assets/icons/removeIcon'
import { paidIconSvg } from '@/assets/icons/paidIcon'

export default {
  name: 'PayoutsLinks',
  components: { FinancePayoutsEmailModal },
  props: {
    templates: {
      type: Array
    },
    item: {
      type: Object,
      default: () => {}
    },
    isRouteUpdate: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      payout: null,
      sendEmailModal: {
        payout: null,
        show: false
      },
      editIconSvg: editIcon(),
      draftIconSvg,
      downloadIconSvg,
      moreVertIconSvg,
      mailIconSvg,
      removeIconSvg: removeIcon(),
      paidIconSvg
    }
  },
  computed: {
    ...mapGetters({
      getIsModalLoading: 'finances/getIsModalLoading'
    }),
    containerId () {
      return `payout-links-${this.payout.id}`
    },
    computedTemplates () {
      if (!this.templates) {
        return []
      }

      return this.templates.filter(({ name }) => {
        /**
         * If item has `payout_template_type` property set to "custom" and `custom_file_name` property (that is the
         * custom template is uploaded) then we need to display only that custom template. Otherwise, we should not
         * display custom template.
         */
        if (this.item.payout_template_type.name === 'custom' && this.item.custom_file_name) {
          return name === 'custom'
        } else {
          return name !== 'custom'
        }
      })
    }
  },
  watch: {
    'item': {
      deep: true,
      immediate: true,
      handler (item) {
        this.payout = item
      }
    }
  },
  methods: {
    pushRoute (payout) {
      const route = this.$router.resolve({
        name: 'finances-payouts-edit',
        params: {
          id: this.payout.id
        }
      })

      window.history.pushState({}, '', route.href)
      this.showEditPayoutsModal(payout)
    },
    showSendPayoutEmailModal (item) {
      this.sendEmailModal.payout = item
      this.sendEmailModal.show = true
    },
    payoutEmailSent (payout) {
      this.payout = cloneDeep(payout)
      this.sendEmailModal.show = false
      this.$emit('updatePayouts')
    },
    showEditPayoutsModal (item) {
      this.$emit('showEditPayoutsModal', item)
    },
    markAsPaid (item) {
      this.$emit('markAsPaid', item)
    },
    async removePayout (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the payout?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        try {
          await this.$api.finances.removePayouts(item.id)
          this.$snotify.success(
            'Payout successfully deleted',
            'Success',
            this.$store.state.snotify
          )
        } catch (e) {
          this.$snotify.error(
            'Error on payout delete',
            'Error',
            this.$store.state.snotify
          )
        }
        this.$emit('deletedPayout', { data: item.id })
        this.$emit('updatePayouts')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .payout-badge {
    font-size: 14px;
    padding: 6px 12px 4px;
    background-color: #DEEBFF;

    &.action-links {
      background-color: transparent;
    }

    &.not-paid{
      background-color: #ede742;
    }

    a, :global(.btn-link) {
      cursor: pointer;
      color: $iconColorAccent;
    }

    a.disabled {
      pointer-events: none;
      opacity: .5;
    }
  }

  .action-link-icon {
      width: 18px;
      display: inline-block;
    }

  .action-links {
    & > *:not(:first-child) {
      margin-left: 5px;
    }
  }

.dropdown-icon {
  width: 18px;
  display: inline-block;
  text-align: center;
  margin-right: 5px;
}

  .material-symbols-outlined {
    font-size: 18px;
  }

  .more-actions-dropdown .material-symbols-outlined {
    color: $iconColorAccent;
    vertical-align: -4px;
    margin-right: 3px;
  }

  .payout-popover-text {
    font-size: 13px;
  }

  .template-link {
    text-decoration: none
  }
  .payout-badge :global(.dropdown .dropdown-toggle.btn-link) {
    width: 20px;
    height: 15px;
    border: 0px;
    line-height: 0;
    .material-icons {
      line-height: 15px;
      font-size: 18px;
    }
  }
</style>
